import { useState } from 'react';
import { Star, Lock, Link2, MoreVertical } from 'lucide-react';
import cn from '../../utils/cn';
import { formatListDate, formatRelative } from '../../utils/format';
import { useIsMobile } from '../../hooks/useMediaQuery';
import FileTypeIcon, { badgeFor } from './FileTypeIcon';
import DocumentActions from './DocumentActions';
import { Checkbox } from '../common/Field';
import { IconButton } from '../common/Button';

/** Column templates per scope — the v2 `.cols-files` grid plus scope-specific variants. */
const COLS = {
  default: { cls: 'cols-files', headers: ['Name', 'Type', 'Version', 'Modified', 'Owner', ''] },
  shared: { style: 'minmax(0,2.4fr) 90px 130px 110px 150px', headers: ['Name', 'Type', 'Shared by', 'Permission', ''] },
  recent: { style: 'minmax(0,2.4fr) 90px 150px 150px', headers: ['Name', 'Type', 'Last opened', ''] },
  trash: { style: 'minmax(0,2.4fr) 90px 130px 130px 150px', headers: ['Name', 'Type', 'Deleted', 'Deleted by', ''] },
};

/**
 * Mobile card row (< 860px): a div, not a button — it contains the `⋮` button and
 * buttons cannot nest.
 */
function MobileRow({ item, actions, onOpen, variant }) {
  const sub =
    variant === 'trash'
      ? `Deleted ${formatRelative(item.deletedAt)}`
      : item.type === 'folder'
        ? `${item.itemCount} items`
        : `v${item.currentVersionNo} · ${formatListDate(item.updatedAt)} · ${item.owner?.name || ''}`;
  return (
    <div
      className="mrow"
      role="button"
      tabIndex={0}
      onClick={() => onOpen(item)}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(item))}
    >
      <div className="cell-name">
        <FileTypeIcon name={item.name} type={item.type} size={item.type === 'folder' ? 'lg' : 'lg'} />
        <div style={{ minWidth: 0 }}>
          <div className="title">{item.name}</div>
          <div className="sub">{sub}</div>
        </div>
      </div>
      <span onClick={(e) => e.stopPropagation()}>
        <DocumentActions item={item} actions={actions} trigger={<IconButton icon={MoreVertical} label="Actions" size={18} style={{ width: 44, height: 44 }} />} />
      </span>
    </div>
  );
}

/** The v2 file list: `.th` column header + `.trow` grid rows inside a `.panel`. */
export default function DocumentTable({ items, selection, onOpen, actions, variant = 'default', showPath = false }) {
  const isMobile = useIsMobile();
  const [openMenuId, setOpenMenuId] = useState(null);
  const cols = COLS[variant] || COLS.default;
  const gridStyle = cols.style ? { gridTemplateColumns: cols.style } : undefined;

  if (isMobile) {
    return (
      <div className="panel panel-list">
        {items.map((item) => (
          <MobileRow key={item.id} item={item} actions={actions} onOpen={onOpen} variant={variant} />
        ))}
      </div>
    );
  }

  return (
    <div className="panel panel-list">
      <div className={cn('th', cols.cls)} style={gridStyle}>
        {cols.headers.map((h, i) => (
          <span key={i}>
            {i === 0 && selection ? (
              <span className="row" style={{ gap: 10 }}>
                <Checkbox checked={selection.allSelected} indeterminate={selection.someSelected} onChange={selection.toggleAll} aria-label="Select all" />
                {h}
              </span>
            ) : (
              h
            )}
          </span>
        ))}
      </div>

      {items.map((item) => {
        const isFolder = item.type === 'folder';
        const selected = selection?.isSelected(item.id);
        return (
          <div
            key={item.id}
            className={cn('trow', cols.cls, selected && 'is-selected')}
            style={gridStyle}
            onClick={() => onOpen(item)}
          >
            <div className="cell-name">
              {selection && (
                <span onClick={(e) => e.stopPropagation()} className="row">
                  <Checkbox checked={Boolean(selected)} onChange={() => selection.toggle(item.id)} aria-label={`Select ${item.name}`} />
                </span>
              )}
              <FileTypeIcon name={item.name} type={item.type} />
              <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="title">{item.name}</span>
                <span className="file-badges">
                  {item.shareCount > 0 && <span className="pill pill-info">Shared</span>}
                  {item.isConfidential && (
                    <span className="pill pill-warn">
                      <Lock size={10} /> Confidential
                    </span>
                  )}
                  {item.hasExternalLink && <Link2 size={13} style={{ color: 'var(--muted)' }} aria-label="External link active" />}
                  {item.ocrStatus === 'processing' && <span className="pill pill-accent">Processing</span>}
                  {item.ocrStatus === 'failed' && <span className="pill pill-err">OCR failed</span>}
                  {item.ocrStatus === 'needs_ocr' && <span className="pill pill-warn">Needs OCR</span>}
                </span>
              </div>
              {showPath && item.path && (
                <span className="sub truncate" style={{ marginLeft: 4 }}>
                  {item.path}
                </span>
              )}
            </div>

            <div className="muted" style={{ fontSize: 13 }}>
              {isFolder ? 'Folder' : badgeFor(item.name).label}
            </div>

            {variant === 'default' && (
              <>
                <div className="muted" style={{ fontSize: 13 }}>
                  {isFolder ? '—' : `v${item.currentVersionNo}`}
                </div>
                <div className="muted" style={{ fontSize: 13 }}>
                  {formatListDate(item.updatedAt)}
                </div>
                <div style={{ fontSize: 13 }} className="truncate">
                  {item.owner?.name || '—'}
                </div>
              </>
            )}
            {variant === 'shared' && (
              <>
                <div style={{ fontSize: 13 }} className="truncate">
                  {item.owner?.name || '—'}
                </div>
                <div>
                  <span className="pill pill-info">{item.capabilities?.includes('download') ? 'Download' : 'View'}</span>
                </div>
              </>
            )}
            {variant === 'recent' && (
              <div className="muted" style={{ fontSize: 13 }}>
                {formatRelative(item.lastViewedAt)}
              </div>
            )}
            {variant === 'trash' && (
              <>
                <div className="muted" style={{ fontSize: 13 }}>
                  {formatListDate(item.deletedAt)}
                </div>
                <div style={{ fontSize: 13 }} className="truncate">
                  {item.deletedBy?.name || '—'}
                </div>
              </>
            )}

            <div className="cell-actions" onClick={(e) => e.stopPropagation()}>
              {!isFolder && !item.deletedAt && (
                <button
                  type="button"
                  className={cn('star-btn', item.isFavorite && 'is-on')}
                  onClick={() => actions.toggleFavorite(item)}
                  aria-label={item.isFavorite ? 'Remove from favourites' : 'Add to favourites'}
                >
                  <Star size={14} fill={item.isFavorite ? 'currentColor' : 'none'} />
                </button>
              )}
              {!item.deletedAt && item.capabilities?.includes('share') && (
                <button type="button" className="link" onClick={() => actions.share(item)}>
                  Share
                </button>
              )}
              {!isFolder && !item.deletedAt && item.capabilities?.includes('download') && (
                <button type="button" className="link" onClick={() => actions.download(item)}>
                  Download
                </button>
              )}
              {item.deletedAt && (
                <button type="button" className="link" onClick={() => actions.restore(item)}>
                  Restore
                </button>
              )}
              <span className={cn('row-actions', openMenuId === item.id && 'is-open')}>
                <DocumentActions item={item} actions={actions} onOpenChange={(open) => setOpenMenuId(open ? item.id : null)} />
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
